/**
 * O COMENTÁRIO DETALHADO de uma resposta.
 *
 * Responde a três perguntas, nesta ordem:
 *
 *  1. **O que você respondeu** — e qual era a resposta certa.
 *  2. **Onde está o erro** — o nome do erro, a descrição do raciocínio equivocado e, o
 *     que só este motor sabe fazer, A CONTA que levou o aluno àquela resposta, com os
 *     números da variante que ele viu. Um distrator nasceu de um solucionador
 *     defeituoso; por isso o sistema consegue reconstituir o caminho, não só constatar
 *     que ele deu errado.
 *  3. **Como resolver** — a resolução comentada, com os mesmos números.
 *
 * É o mesmo componente no laboratório, no percurso e na revisão do fim do percurso:
 * quem escreve o item vê exatamente o que o aluno vai ler.
 */

import { formatBR } from '../core/num.ts';
import { capitalizar, conta, lista, osN, verbo } from '../core/pt.ts';
import { ERROR_CATALOG } from '../schema/errors.ts';
import type { ItemInstance } from '../schema/item.ts';
import type { GradingResult, Response } from '../schema/response.ts';
import { toPlainText, type RichText } from '../schema/richtext.ts';
import { renderRichText } from './richtext.ts';

export function renderComentario(
  inst: ItemInstance,
  r: GradingResult,
  resposta: Response | null,
): HTMLElement {
  const root = document.createElement('section');
  root.className = `comentario ${r.correct ? 'is-ok' : r.score > 0 ? 'is-parcial' : 'is-erro'}`;
  root.setAttribute('aria-label', 'Comentário detalhado');

  // --- 1. sua resposta --------------------------------------------------------
  const sua = bloco('Sua resposta');
  for (const p of suaResposta(inst, r, resposta)) sua.appendChild(paragrafo(p));
  // Em construção a análise descreve a própria figura do aluno — é "sua resposta",
  // mesmo quando ela está certa ("marcou exatamente os 7 pontos").
  const naFigura = inst.interaction.kind === 'construct' || inst.interaction.kind === 'drag-points';
  if (naFigura && r.analysis && r.correct) {
    sua.appendChild(renderRichText(r.analysis, 'p'));
  }
  root.appendChild(sua);

  // --- 2. onde está o erro ----------------------------------------------------
  if (!r.correct) {
    const onde = bloco(naFigura ? 'O que o corretor encontrou' : 'Onde está o erro');
    const codigo = r.evidence.matchedError;
    const anchor = codigo ? ERROR_CATALOG[codigo] : undefined;

    if (anchor) {
      const erro = document.createElement('div');
      erro.className = 'comentario-erro';
      const nome = document.createElement('p');
      nome.className = 'comentario-erro-nome';
      nome.textContent = anchor.label;
      const desc = document.createElement('p');
      desc.textContent = anchor.description;
      erro.append(nome, desc);
      onde.appendChild(erro);
    }

    if (r.analysis) {
      // "A conta que leva à sua resposta" só quando HÁ uma conta reconstituída — isto
      // é, quando a resposta casou com um erro conhecido. Sem isso, a análise diz
      // justamente que não há conta a mostrar, e o subtítulo mentiria.
      if (!naFigura && inst.interaction.kind !== 'error-step' && anchor) {
        const rot = document.createElement('p');
        rot.className = 'comentario-subrotulo';
        rot.textContent = 'A conta que leva à sua resposta';
        onde.appendChild(rot);
      }
      const conta = renderRichText(r.analysis, 'p');
      conta.classList.add('comentario-conta');
      onde.appendChild(conta);
    } else if (!anchor) {
      onde.appendChild(
        paragrafo(
          'Esta resposta não corresponde a nenhum dos erros típicos catalogados para este item. ' +
            'Compare o seu caminho com a resolução abaixo.',
        ),
      );
    }

    // Crédito parcial: cada critério, com ✓ no que já foi cumprido. Mostrar só o que
    // falta desanimaria e esconderia que parte do caminho foi feita.
    if (r.evidence.rubric.length > 0) {
      const rot = document.createElement('p');
      rot.className = 'comentario-subrotulo';
      rot.textContent = 'Critérios de crédito parcial';
      const ul = document.createElement('ul');
      ul.className = 'comentario-rubrica';
      for (const c of r.evidence.rubric) {
        const li = document.createElement('li');
        li.className = c.earned > 0 ? 'is-ok' : 'is-falta';
        li.textContent = c.criterion;
        ul.appendChild(li);
      }
      onde.append(rot, ul);
    }

    root.appendChild(onde);
  }

  // --- 3. como resolver -------------------------------------------------------
  // N4 é construção; N3, marcação na figura — o comentário usa a palavra de cada um.
  const como = bloco(inst.level === 'N4' ? 'Como construir' : 'Como resolver');
  const resolucao = renderRichText(inst.rationale, 'div');
  resolucao.classList.add('comentario-resolucao');
  como.appendChild(resolucao);
  root.appendChild(como);

  return root;
}

// ---------------------------------------------------------------------------

function suaResposta(inst: ItemInstance, r: GradingResult, resposta: Response | null): string[] {
  if (!resposta || resposta.kind === 'omitted') return ['Você não respondeu a este item.'];

  const inter = inst.interaction;

  if (inter.kind === 'select-one' && resposta.kind === 'select-one') {
    // Numa alternativa-gráfico (E6) o texto visível é vazio: fala-se do gráfico.
    const nome = (id: string): string => {
      const alt = inter.alternatives.find((a) => a.id === id);
      // Sem o ponto final: a alternativa que é frase ("… Fábrica 4.") entra no meio de
      // outra, e saía "Fábrica 4., que é a resposta correta" (apontado pelo autor).
      const texto = alt ? toPlainText(alt.content).trim().replace(/\.$/, '') : '';
      return alt?.figure && texto === ''
        ? `o gráfico ${id}`
        : texto === ''
          ? `a alternativa ${id}`
          : `a alternativa ${id} — ${texto}`;
    };
    if (r.correct) return [`Você marcou ${nome(resposta.choiceId)}, que é a resposta correta.`];
    return [
      `Você marcou ${nome(resposta.choiceId)}.`,
      `A resposta correta é ${nome(inter.keyId)}.`,
    ];
  }

  if (inter.kind === 'numeric-entry' && resposta.kind === 'numeric-entry') {
    const unidade = inter.unit ? ` ${inter.unit}` : '';
    const verbo = inter.viaControl ? 'escolheu' : 'respondeu';
    const digitado = resposta.raw.trim() === '' ? '(em branco)' : resposta.raw.trim();
    // Com faixa de respostas, não existe "a" resposta certa: diz-se a faixa inteira.
    const certa = inter.accept
      ? `Qualquer valor de ${formatBR(inter.accept.lo, inter.decimals)} a ` +
        `${formatBR(inter.accept.hi, inter.decimals)}${unidade} estaria correto.`
      : `A resposta correta é ${formatBR(inter.keyValue, inter.decimals)}${unidade}.`;
    if (r.correct) {
      return inter.accept
        ? [`Você ${verbo} ${digitado}${unidade}, e esse valor está correto.`, certa]
        : [`Você ${verbo} ${digitado}${unidade}, que é a resposta correta.`];
    }
    return [`Você ${verbo} ${digitado}${unidade}.`, certa];
  }

  if (inter.kind === 'coordinate-entry' && resposta.kind === 'coordinate-entry') {
    const nomes = inter.pontos.filter((p) => !p.valor).map((p) => p.nome);
    const campo = (s: string | undefined): string => (s && s.trim() !== '' ? s.trim() : '?');
    const escritos = nomes
      .map((n, i) => `${n} = (${campo(resposta.raw[i]?.[0])}, ${campo(resposta.raw[i]?.[1])})`)
      .join(' e ');
    if (r.correct) return [`Você escreveu ${escritos}, e está correto.`];
    const certa = inter.respostas[0] ?? [];
    const certaTxt = nomes.map((n, i) => `${n} = ${pontoBR(certa[i])}`).join(' e ');
    return [
      `Você escreveu ${escritos}.`,
      `A resposta correta é ${certaTxt}${nomes.length > 1 ? ' — em qualquer ordem entre os pontos pedidos' : ''}.`,
    ];
  }

  // Havia duas cópias idênticas deste bloco, uma depois da outra — a segunda nunca
  // rodava —, e as duas diziam "sacos" também na variante dos potes.
  if (inter.kind === 'balance-ops' && resposta.kind === 'balance-ops') {
    const e = resposta.estado;
    const prato = (p: { sacos: number; pesos: number }): string => {
      const partes: string[] = [];
      if (p.sacos > 0) partes.push(String(conta(p.sacos, inter.recipiente)));
      if (p.pesos > 0) partes.push(String(conta(p.pesos, 'peso')));
      return partes.length > 0 ? partes.join(' e ') : 'nada';
    };
    return [
      `Você fez ${conta(resposta.acoes.length, 'movimento')} e parou com ${prato(e.esq)} de um lado e ${prato(e.dir)} do outro.`,
    ];
  }

  if (inter.kind === 'region-select' && resposta.kind === 'region-select') {
    const rot = (id: string): string => inter.regioes.find((x) => x.id === id)?.rotulo ?? id;
    const n = resposta.selecionadas.length;
    const marcou =
      n === 0
        ? 'Você não marcou nada.'
        : `Você marcou ${conta(n, inter.oQue)}: ${lista(resposta.selecionadas.map(rot))}.`;
    const pedidas = osN(inter.oQue).adj('pedido');
    if (r.correct) return [marcou, `São exatamente ${pedidas}.`];
    return [
      marcou,
      `${capitalizar(pedidas)} ${verbo('ser.imperf', 'pl')} ${inter.chave.length}: ${lista(inter.chave.map(rot))}.`,
    ];
  }

  if (inter.kind === 'control-fit' && resposta.kind === 'control-fit') {
    const s = inst.stimuli.find((x) => x.id === inter.stimulusId);
    const partes =
      s?.kind === 'manipulable'
        ? s.controls
            .filter((c) => inter.controles.includes(c.id))
            .map(
              (c) => `${toPlainText(c.label)} = ${c.format(resposta.valores[c.id] ?? c.initial)}`,
            )
        : [];
    const onde =
      partes.length > 0
        ? `Você deixou os controles em ${partes.join(' e ')}.`
        : 'Você ajustou os controles da figura.';
    if (r.correct) return [onde, 'A figura ficou como o pedido.'];
    if (r.score > 0)
      return [
        onde,
        `A figura ainda não ficou como o pedido, mas a resposta vale ${Math.round(r.score * 100)}% da pontuação.`,
      ];
    return [onde, 'A figura ainda não ficou como o pedido.'];
  }

  if (inter.kind === 'expression-entry' && resposta.kind === 'expression-entry') {
    const escrito = `${inter.prefixo} ${resposta.raw.trim()}`;
    if (r.correct) return [`Você escreveu ${escrito}, que está correto.`];
    return [
      `Você escreveu ${escrito}.`,
      `Uma resposta correta é ${inter.prefixo} ${inter.chave} — ou qualquer expressão equivalente.`,
    ];
  }

  if (inter.kind === 'order' && resposta.kind === 'order') {
    const nome = (i: number): string => toPlainText(inter.itens[i] ?? []).trim();
    const sua = resposta.ordem.map(nome).join(' → ');
    if (r.correct) return [`Você ordenou: ${sua}. Está correto.`];
    return [`Você ordenou: ${sua}.`, `A ordem certa é: ${inter.ordem.map(nome).join(' → ')}.`];
  }

  if (inter.kind === 'error-step' && resposta.kind === 'error-step') {
    const n = resposta.passo + 1;
    const k = inter.passoErrado + 1;
    if (r.correct) return [`Você marcou o passo ${n}, que é o primeiro passo errado da resolução.`];
    return [`Você marcou o passo ${n}.`, `O primeiro passo errado é o passo ${k}.`];
  }

  if (inter.kind === 'drag-points') {
    // Colunas têm altura; marcadores e pontos, posição.
    const barras = inter.desenho === 'barras';
    const nome = barras
      ? 'As colunas'
      : inter.desenho === 'marcadores'
        ? 'Os marcadores'
        : 'Os pontos';
    if (r.correct)
      return [
        barras ? 'As colunas ficaram nas alturas certas.' : `${nome} ficaram nas posições certas.`,
      ];
    if (r.score > 0) {
      return [
        `${nome} ainda não estão ${barras ? 'todas' : 'todos'} no lugar, mas a resposta vale ${Math.round(r.score * 100)}% da pontuação.`,
      ];
    }
    return [
      barras
        ? 'As colunas ainda não estão nas alturas pedidas.'
        : `${nome} ainda não estão nas posições pedidas.`,
    ];
  }

  if (inter.kind === 'construct') {
    const oQue = inst.level === 'N4' ? 'Sua construção' : 'Sua marcação';
    if (r.correct && r.estrelas) {
      const e = r.estrelas;
      return [
        `${oQue} está correta, com ${conta(e.movimentos, 'movimento')}.`,
        e.movimentos <= e.meta
          ? `Está dentro da meta de ${conta(e.meta, 'movimento')}: três estrelas.`
          : `A construção mais curta que conhecemos usa ${conta(e.meta, 'movimento')}. Dá para chegar lá reaproveitando os cruzamentos que já existem, em vez de criar pontos à parte.`,
      ];
    }
    if (r.correct) return [`${oQue} está correta.`];
    if (r.score > 0) {
      return [
        `${oQue} ainda não cumpre o pedido, mas vale ${Math.round(r.score * 100)}% da pontuação.`,
      ];
    }
    return [`${oQue} ainda não cumpre o pedido.`];
  }

  return [];
}

function pontoBR(p: { readonly x: number; readonly y: number } | undefined): string {
  if (!p) return '?';
  return `(${formatBR(p.x)}, ${formatBR(p.y)})`;
}

function bloco(titulo: string): HTMLElement {
  const b = document.createElement('div');
  b.className = 'comentario-bloco';
  const h = document.createElement('h3');
  h.className = 'comentario-rotulo';
  h.textContent = titulo;
  b.appendChild(h);
  return b;
}

function paragrafo(texto: string | RichText): HTMLElement {
  if (typeof texto !== 'string') return renderRichText(texto, 'p');
  const p = document.createElement('p');
  p.textContent = texto;
  return p;
}
